import React from 'react';

import {useStore} from 'effector-react';

import {useTranslation} from 'react-i18next';

import {ReactComponent as CheckIcon} from 'assets/svg/check.svg';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {DocumentStatus} from 'core/dictionaries';

import {
    $allowedActionsForInvoice,
    $doesInvoiceExist,
    $invoiceStatusInfo,
    AllowedActionsForInvoice,
    setTargetStatus,
    updateInvoiceStatusFx,
} from 'models/documents/invoices/common';

import {$invoiceCreatingPending} from 'models/documents/invoices/details';

import {StatusButtonsWrapper} from './styled';

const CustomerButtons = ({
    changeInvoiceStatus,
    allowedActionsForInvoice,
    invoiceCreatingPending,
    t,
}: {
    changeInvoiceStatus: (to: DocumentStatus) => void;
    allowedActionsForInvoice: AllowedActionsForInvoice;
    invoiceCreatingPending: boolean;
    t: (key: string) => string;
}) => (
    <>
        {(allowedActionsForInvoice.checkFail || allowedActionsForInvoice.reject) && (
            <Button
                disabled={invoiceCreatingPending}
                style={{whiteSpace: 'nowrap'}}
                variant={ButtonVariant.Cancel}
                size="sm"
                type="button"
                onClick={() => {
                    changeInvoiceStatus(
                        allowedActionsForInvoice.checkFail
                            ? DocumentStatus['CHECK_FAIL']
                            : DocumentStatus['REJECTED'],
                    );
                }}
            >
                {allowedActionsForInvoice.checkFail ? t('CHECK_FAIL') : t('REJECT')}
                <Icon style={{marginLeft: 6}} source={CrossIcon} width={10} height={10} />
            </Button>
        )}
        {(allowedActionsForInvoice.checkSuccess || allowedActionsForInvoice.accept) && (
            <Button
                disabled={invoiceCreatingPending}
                style={{whiteSpace: 'nowrap'}}
                variant={ButtonVariant.Normal}
                size="sm"
                type="button"
                onClick={() => {
                    changeInvoiceStatus(
                        allowedActionsForInvoice.checkSuccess
                            ? DocumentStatus['CHECK_SUCCESS']
                            : DocumentStatus['ACCEPTED'],
                    );
                }}
            >
                {allowedActionsForInvoice.checkSuccess ? t('CHECK_SUCCESS') : t('ACCEPT')}
                <Icon style={{marginLeft: 6}} source={CheckIcon} width={16} height={16} />
            </Button>
        )}
    </>
);

const SupplierButtons = ({
    changeInvoiceStatus,
    allowedActionsForInvoice,
    invoiceCreatingPending,
    t,
    doesInvoiceExist,
}: {
    changeInvoiceStatus: (to: DocumentStatus) => void;
    allowedActionsForInvoice: AllowedActionsForInvoice;
    invoiceCreatingPending: boolean;
    t: (key: string) => string;
    doesInvoiceExist: boolean;
}) => (
    <>
        {allowedActionsForInvoice.sendToApproval && doesInvoiceExist && (
            <Button
                style={{whiteSpace: 'nowrap'}}
                disabled={invoiceCreatingPending}
                size="sm"
                variant={
                    allowedActionsForInvoice.approve ||
                    allowedActionsForInvoice.sendToBuyer ||
                    allowedActionsForInvoice.return
                        ? ButtonVariant.Secondary
                        : ButtonVariant.Normal
                }
                onClick={() => {
                    changeInvoiceStatus(DocumentStatus['UNDER_APPROVAL']);
                }}
                type="button"
            >
                {t('SEND_TO_APPROVAL')}
            </Button>
        )}
        {allowedActionsForInvoice.return && (
            <Button
                style={{whiteSpace: 'nowrap'}}
                disabled={invoiceCreatingPending}
                size="sm"
                variant={
                    allowedActionsForInvoice.approve || allowedActionsForInvoice.sendToBuyer
                        ? ButtonVariant.Secondary
                        : ButtonVariant.Normal
                }
                onClick={() => {
                    changeInvoiceStatus(DocumentStatus['RETURNED']);
                }}
                type="button"
            >
                {t('RETURN')}
            </Button>
        )}
        {(allowedActionsForInvoice.approve || allowedActionsForInvoice.sendToBuyer) && (
            <Button
                style={{whiteSpace: 'nowrap'}}
                disabled={invoiceCreatingPending}
                size="sm"
                variant={ButtonVariant.Normal}
                onClick={() => {
                    changeInvoiceStatus(
                        allowedActionsForInvoice.approve
                            ? DocumentStatus['APPROVED']
                            : DocumentStatus['PENDING'],
                    );
                }}
                type="button"
            >
                {allowedActionsForInvoice.approve ? t('APPROVE') : t('SEND_TO_BUYER')}
            </Button>
        )}
    </>
);

export const ControlButtons = ({id}: {id: string | undefined}) => {
    const invoiceStatusInfo = useStore($invoiceStatusInfo);
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);
    const invoiceCreatingPending = useStore($invoiceCreatingPending);
    const doesInvoiceExist = useStore($doesInvoiceExist);
    const {t: translate} = useTranslation();
    const t = (key: string) => translate('dictionaries.documents.statusInvoice.' + key);

    function changeInvoiceStatus(to: DocumentStatus) {
        if (invoiceStatusInfo && id) {
            setTargetStatus(to);
            updateInvoiceStatusFx({
                invoiceId: id,
                status: to,
            });
        }
    }
    return (
        <StatusButtonsWrapper>
            <SupplierButtons
                changeInvoiceStatus={changeInvoiceStatus}
                allowedActionsForInvoice={allowedActionsForInvoice}
                invoiceCreatingPending={invoiceCreatingPending}
                t={t}
                doesInvoiceExist={doesInvoiceExist}
            />
            <CustomerButtons
                changeInvoiceStatus={changeInvoiceStatus}
                allowedActionsForInvoice={allowedActionsForInvoice}
                invoiceCreatingPending={invoiceCreatingPending}
                t={t}
            />
        </StatusButtonsWrapper>
    );
};
